<script lang="ts">
  import { popup, type PopupSettings } from '@skeletonlabs/skeleton'

  import Input from '@components/Input.svelte'
  import HuggingFaceTokenPicker from '@routes/huggingface-tokens/HuggingFaceTokenPicker.svelte'
  import type { HuggingFaceDataset } from '@routes/llm/runs/llm-run.svelte'
  import { huggingfaceRepoNameValidator, type ValidityState } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    dataset: HuggingFaceDataset
    fieldId: string
    label: string
    invalidState: ValidityState
    sharedTokenEnabled: boolean
    required?: boolean
    onNameChange: (value: string) => void
  }

  let {
    dataset,
    fieldId,
    label,
    invalidState,
    sharedTokenEnabled,
    required = false,
    onNameChange,
  }: Props = $props()

  const id = $props.id()
  const infoPopup: PopupSettings = {
    event: 'hover',
    placement: 'right',
    target: `${id}-hugging-face-dataset-info`,
  }
</script>

<label class="pb-1" for={fieldId}>
  {label}
  <button disabled class="[&>*]:pointer-events-none" use:popup={infoPopup}>
    <Icon icon="iconoir:info-circle-solid" class="h-4 w-4" />
  </button>
  <div class="card variant-filled-secondary z-50 p-4" data-popup={infoPopup.target}>
    The HuggingFace dataset name is made up of the organization name or user name and the dataset
    name separated by a slash. e.g. "<code>hirundo-io/TruthfulQA-hallucinations-free-text</code>"
  </div>
</label>
<Input
  type="text"
  id={fieldId}
  value={dataset.huggingFaceDatasetName}
  oninvalid={huggingfaceRepoNameValidator(invalidState)}
  onchange={(event) => {
    onNameChange(event.currentTarget.value)
  }}
  {required}
/>
{#if sharedTokenEnabled}
  <p class="text-sm text-surface-700 dark:text-surface-200">
    Using the shared HuggingFace token for this run.
  </p>
{:else}
  <HuggingFaceTokenPicker
    bind:token={dataset.token}
    bind:tokenId={dataset.tokenId}
    bind:saveToken={dataset.saveToken}
    bind:tokenName={dataset.tokenName}
    customLabel="HuggingFace token (optional)"
  />
{/if}
